import { useEffect } from 'react';

type PageSeoProps = {
  title: string;
  description: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  schema?: Record<string, unknown> | Array<Record<string, unknown>>;
};

function upsertMeta(attribute: 'name' | 'property', key: string, content: string) {
  const selector = `meta[${attribute}="${key}"]`;
  let tag = document.head.querySelector<HTMLMetaElement>(selector);

  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', content);
}

function upsertCanonical(url: string) {
  let tag = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', 'canonical');
    document.head.appendChild(tag);
  }

  tag.setAttribute('href', url);
}

function upsertJsonLd(schema: Record<string, unknown> | Array<Record<string, unknown>>) {
  const scriptId = 'page-json-ld';
  let tag = document.head.querySelector<HTMLScriptElement>(`script#${scriptId}`);

  if (!tag) {
    tag = document.createElement('script');
    tag.id = scriptId;
    tag.type = 'application/ld+json';
    document.head.appendChild(tag);
  }

  tag.textContent = JSON.stringify(schema);
}

export default function PageSeo({
  title,
  description,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  schema,
}: PageSeoProps) {
  useEffect(() => {
    document.title = title;
    upsertMeta('name', 'description', description);
    upsertMeta('property', 'og:title', ogTitle || title);
    upsertMeta('property', 'og:description', ogDescription || description);
    upsertMeta('property', 'og:type', ogType);

    if (ogImage) {
      upsertMeta('property', 'og:image', ogImage);
    }

    if (canonicalUrl) {
      upsertCanonical(canonicalUrl);
      upsertMeta('property', 'og:url', canonicalUrl);
    }

    if (schema) {
      upsertJsonLd(schema);
    }

    return () => {
      const script = document.head.querySelector<HTMLScriptElement>('script#page-json-ld');
      if (script) {
        script.remove();
      }
    };
  }, [canonicalUrl, description, ogDescription, ogImage, ogTitle, ogType, schema, title]);

  return null;
}